import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, Truck, MapPin, ShieldAlert, Send } from "lucide-react";
import { toast } from "sonner";
import { EmptyState } from "../../components/shared/EmptyState";
import { api, formatApiError } from "../../lib/api";
import { getCart, updateCartItem, removeFromCart, getCartMode, setCartMode, getPromoCode, setPromoCode as storePromoCode } from "../../lib/cart";

export default function SelectionPage() {
  const [items, setItems] = useState(() => getCart());
  const [mode, setModeState] = useState(() => getCartMode());
  const [promoInput, setPromoInput] = useState(() => getPromoCode());
  const [promo, setPromo] = useState(null);
  const [promoError, setPromoError] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const setMode = (m) => { setModeState(m); setCartMode(m); };

  const updateQty = (slug, delta) => {
    const current = getCart().find((i) => i.slug === slug);
    setItems(updateCartItem(slug, (current?.qty || 1) + delta));
  };
  const removeItem = (slug) => setItems(removeFromCart(slug));

  const itemsPayload = () => items.map((i) => ({ product_slug: i.slug, variant_id: i.variant_id || null, qty: i.qty, option_ids: i.option_ids || [], option_choice_ids: i.option_choice_ids || [] }));
  const applyPromo = async (code = promoInput, silent = false) => {
    if (!code.trim() || items.length === 0) return;
    setPromoLoading(true);
    setPromoError(null);
    try {
      const { data } = await api.post("/promotions/validate", { code: code.trim(), items: itemsPayload() });
      setPromo(data);
      setPromoInput(data.code);
      storePromoCode(data.code);
      if (!silent) toast.success(`Code ${data.code} appliqué : -${data.discount_amount.toFixed(2)} € sur le matériel`);
    } catch (e) {
      setPromo(null);
      storePromoCode("");
      const message = formatApiError(e.response?.data?.detail);
      setPromoError(message);
      if (!silent) toast.error(message);
    } finally { setPromoLoading(false); }
  };
  const removePromo = () => { setPromo(null); setPromoInput(""); setPromoError(null); storePromoCode(""); };

  useEffect(() => {
    const code = getPromoCode();
    if (code && items.length > 0) applyPromo(code, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const materialTotal = items.reduce((sum, it) => sum + it.price * it.qty, 0);
  const optionsTotal = items.reduce((sum, it) => sum + (it.option_choices || []).reduce((x, ch) => x + (ch.per_unit ? ch.price_ttc * it.qty : ch.price_ttc), 0), 0);
  const discount = promo?.discount_amount || 0;
  const delivery = 0; // tarif de livraison confirmé par Art Event (service de distance À DÉFINIR)
  const totalTTC = Math.max(0, materialTotal + optionsTotal + delivery - discount);
  const acompte = totalTTC * 0.3;
  const caution = items.reduce((sum, it) => sum + (it.caution_amount || 0) * it.qty, 0);

  return (
    <div className="container-ae py-12 md:py-16" data-testid="selection-page">
      <h1 className="text-3xl sm:text-4xl text-deepgreen" data-testid="selection-title">Ma sélection</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Votre sélection n'est pas encore une réservation : Art Event vérifie chaque demande avant validation.
      </p>
      <div className="mt-4">
      </div>

      {items.length === 0 ? (
        <div className="mt-8">
          <EmptyState title="Votre sélection est vide" action={<Link to="/boutique" className="btn-primary-ae">Découvrir la collection</Link>}>
            Ajoutez des articles depuis la boutique pour composer votre événement.
          </EmptyState>
        </div>
      ) : (
      <div className="mt-8 grid gap-10 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {items.map((item) => (
            <div key={item.slug} className="card-ae flex gap-5 p-5" data-testid={`selection-item-${item.slug}`}>
              <img src={item.image} alt={item.name} className="h-24 w-24 rounded-lg object-cover" />
              <div className="flex flex-1 flex-col">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-heading text-lg text-deepgreen">{item.name}</h3>
                    <p className="mt-0.5 text-sm text-muted-foreground">{item.price.toFixed(2)} € TTC / weekend</p>
                    {(item.option_choices || []).length > 0 && (
                      <p className="mt-0.5 text-xs font-semibold text-gold" data-testid={`selection-choices-${item.slug}`}>
                        {item.option_choices.map((ch) => `${ch.group_name} : ${ch.choice_name}`).join(" · ")}
                      </p>
                    )}
                  </div>
                  <button type="button" onClick={() => removeItem(item.slug)} className="p-2 text-muted-foreground transition-colors hover:text-red-600" data-testid={`remove-item-${item.slug}`} aria-label="Retirer l'article">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-auto flex items-center justify-between pt-3">
                  <div className="flex items-center rounded-full border border-border">
                    <button type="button" onClick={() => updateQty(item.slug, -1)} className="p-2 text-deepgreen" data-testid={`qty-dec-${item.slug}`} aria-label="Diminuer">
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <span className="w-8 text-center text-sm font-bold text-deepgreen" data-testid={`qty-${item.slug}`}>{item.qty}</span>
                    <button type="button" onClick={() => updateQty(item.slug, 1)} className="p-2 text-deepgreen" data-testid={`qty-inc-${item.slug}`} aria-label="Augmenter">
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <p className="text-base font-semibold text-deepgreen" data-testid={`line-total-${item.slug}`}>{(item.price * item.qty + (item.option_choices || []).reduce((s, ch) => s + (ch.per_unit ? ch.price_ttc * item.qty : ch.price_ttc), 0)).toFixed(2)} €</p>
                </div>
              </div>
            </div>
          ))}

          <div className="card-ae p-6" data-testid="selection-event-info">
            <h3 className="font-heading text-xl text-deepgreen">Votre événement</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="label-ae" htmlFor="sel-date-debut">Début de location</label>
                <input id="sel-date-debut" type="date" className="input-ae" data-testid="selection-date-start" />
              </div>
              <div>
                <label className="label-ae" htmlFor="sel-date-fin">Fin de location</label>
                <input id="sel-date-fin" type="date" className="input-ae" data-testid="selection-date-end" />
              </div>
              <div className="sm:col-span-2">
                <label className="label-ae" htmlFor="sel-lieu">Lieu de réception</label>
                <input id="sel-lieu" className="input-ae" placeholder="Adresse du lieu de l'événement" data-testid="selection-venue" />
                <p className="mt-1.5 text-xs text-muted-foreground">Distance depuis Artres : <span className="font-semibold text-deepgreen">calculée à la saisie</span> — au-delà de 80 km, contactez-nous pour une étude exceptionnelle.</p>
              </div>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setMode("retrait")}
                data-testid="mode-retrait"
                className={`flex items-center gap-3 rounded-lg border p-4 text-left transition-colors ${mode === "retrait" ? "border-deepgreen bg-sage-light" : "border-border bg-white hover:border-sage"}`}
              >
                <MapPin className="h-5 w-5 text-deepgreen" />
                <div>
                  <p className="text-sm font-bold text-deepgreen">Retrait au dépôt</p>
                  <p className="text-xs text-muted-foreground">Gratuit, sur rendez-vous — Artres</p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setMode("livraison")}
                data-testid="mode-livraison"
                className={`flex items-center gap-3 rounded-lg border p-4 text-left transition-colors ${mode === "livraison" ? "border-deepgreen bg-sage-light" : "border-border bg-white hover:border-sage"}`}
              >
                <Truck className="h-5 w-5 text-deepgreen" />
                <div>
                  <p className="text-sm font-bold text-deepgreen">Livraison</p>
                  <p className="text-xs text-muted-foreground">20 km offerts puis 1,10 €/km A-R — offerte dès 250 €</p>
                </div>
              </button>
            </div>
            <div className="mt-5">
              <label className="label-ae" htmlFor="sel-promo">Code avantage</label>
              <div className="flex gap-2">
                <input id="sel-promo" className="input-ae" placeholder="Entrez votre code" value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())} data-testid="promo-code-input" />
                <button type="button" onClick={() => applyPromo()} disabled={promoLoading || !promoInput.trim()} className="btn-secondary-ae !px-5 !py-2.5 !text-xs disabled:opacity-50" data-testid="promo-apply-button">{promoLoading ? "Vérification…" : "Appliquer"}</button>
              </div>
              {promo && <p className="mt-2 rounded-lg bg-sage-light px-3 py-2 text-xs font-semibold text-deepgreen" data-testid="promo-success">Code {promo.code} : -{promo.discount_amount.toFixed(2)} € sur le matériel TTC. <button type="button" onClick={removePromo} className="underline" data-testid="promo-remove-button">Retirer</button></p>}
              {promoError && <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-700" data-testid="promo-error">{promoError}</p>}
            </div>
          </div>
        </div>

        <aside className="lg:sticky lg:top-24 h-fit">
          <div className="card-ae p-6" data-testid="selection-summary">
            <h3 className="font-heading text-xl text-deepgreen">Récapitulatif</h3>
            <dl className="mt-4 space-y-2.5 text-sm">
              <div className="flex justify-between"><dt className="text-muted-foreground">Matériel</dt><dd className="font-semibold" data-testid="summary-material">{materialTotal.toFixed(2)} €</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Options</dt><dd className="font-semibold" data-testid="summary-options">{optionsTotal.toFixed(2)} €</dd></div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Livraison</dt>
                <dd className="font-semibold" data-testid="summary-delivery">{mode === "retrait" ? "Gratuit (retrait dépôt)" : materialTotal >= 250 ? "Offerte (≥ 250 €)" : "Tarif confirmé par Art Event"}</dd>
              </div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Remise matériel</dt><dd className="font-semibold text-sage" data-testid="summary-discount">{discount > 0 ? `-${discount.toFixed(2)} €` : "—"}</dd></div>
              <div className="flex justify-between border-t border-border pt-3 text-base">
                <dt className="font-bold text-deepgreen">Total TTC</dt>
                <dd className="font-heading font-bold text-deepgreen" data-testid="summary-total">{totalTTC.toFixed(2)} €</dd>
              </div>
              <div className="flex justify-between rounded-lg bg-gold-light px-3 py-2.5">
                <dt className="font-bold text-deepgreen">Acompte 30 %</dt>
                <dd className="font-heading font-bold text-deepgreen" data-testid="summary-deposit">{acompte.toFixed(2)} €</dd>
              </div>
            </dl>

            {/* CAUTION — visuellement SÉPARÉE du total commercial */}
            <div className="mt-5 rounded-lg border-2 border-dashed border-sage bg-cream p-4" data-testid="summary-caution">
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-deepgreen">
                <ShieldAlert className="h-4 w-4 text-sage" /> Caution — séparée
              </p>
              <p className="mt-1.5 text-sm text-muted-foreground">
                <span className="font-semibold text-deepgreen">{caution.toFixed(2)} €</span> — montant distinct, non encaissé comme chiffre d'affaires. Mode de caution à définir techniquement.
              </p>
            </div>

            <Link to="/demande" className="btn-primary-ae mt-6 w-full" data-testid="send-request-button">
              <Send className="h-4 w-4" /> Envoyer ma demande
            </Link>
            <p className="mt-3 text-center text-xs text-muted-foreground">
              Aucun paiement n'est demandé à cette étape. Votre demande sera vérifiée par Art Event sous 24 h.
            </p>
          </div>
        </aside>
      </div>
      )}
    </div>
  );
}
